import { UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts'
import appConfig from '../config'
import { LOGIN_PATH, LOGIN_REDIRECT_PATH } from '../constants/RoutePaths'
import { setActiveAccessToken } from './AuthorizationHelpers'

const RENEWAL_BUFFER_MINUTES = 12
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 2000
const INVALID_GRANT_ERROR = 'invalid_grant'

const config: UserManagerSettings = {
  authority: appConfig.identityServer.authority,
  client_id: appConfig.identityServer.client_id,
  redirect_uri: `${window.location.origin}${LOGIN_REDIRECT_PATH}`,
  client_secret: appConfig.identityServer.clientSecret,
  scope: appConfig.identityServer.scope,
  response_type: 'code',
  post_logout_redirect_uri: `${window.location.origin}${LOGIN_PATH}`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: false,
  silent_redirect_uri: `${window.location.origin}/silentrenew.html`,
  accessTokenExpiringNotificationTimeInSeconds: 720,
  includeIdTokenInSilentRenew: true,
  revokeTokensOnSignout: true,
  silentRequestTimeoutInSeconds: 60,
}

const configuration = new UserManager(config)

class TokenManager {
  private renewalTimer: NodeJS.Timeout | null = null
  private isRenewing = false
  private retryCount = 0
  private isInitialized = false
  private lastRenewalPromise: Promise<void> | null = null

  constructor(private userManager: UserManager) {
    this.setupEventHandlers()
  }

  private setupEventHandlers() {
    this.userManager.events.addUserLoaded(userDetails => {
      setActiveAccessToken(userDetails.access_token, userDetails.expires_at * 1000)
      if (!this.isInitialized) {
        this.scheduleProactiveRenewal(userDetails.expires_at * 1000)
      }
    })

    this.userManager.events.addSilentRenewError(_error => {
      this.handleRenewalFailure()
    })
  }

  public initializeRenewalSchedule(expiresAt: number) {
    this.isInitialized = true
    this.scheduleProactiveRenewal(expiresAt)
  }

  private scheduleProactiveRenewal(expiresAt: number) {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer)
      this.renewalTimer = null
    }

    const renewalTime = expiresAt - RENEWAL_BUFFER_MINUTES * 60 * 1000
    const currentTime = Date.now()
    const timeUntilRenewal = renewalTime - currentTime

    if (timeUntilRenewal > 0) {
      this.renewalTimer = setTimeout(() => {
        this.performProactiveRenewal()
      }, timeUntilRenewal)
    } else {
      this.performProactiveRenewal()
    }
  }

  private async performProactiveRenewal(): Promise<void> {
    if (this.isRenewing && this.lastRenewalPromise) {
      return this.lastRenewalPromise
    }

    if (this.isRenewing) {
      return
    }

    this.isRenewing = true
    this.lastRenewalPromise = this.doRenewal()

    await this.lastRenewalPromise
    this.isRenewing = false
    this.lastRenewalPromise = null
  }

  private async doRenewal(): Promise<void> {
    try {
      const user = await this.userManager.signinSilent()
      if (user && !user.expired) {
        setActiveAccessToken(user.access_token, user.expires_at * 1000)
        this.scheduleProactiveRenewal(user.expires_at * 1000)
        this.retryCount = 0
      }
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || ''
      if (errorMessage.includes(INVALID_GRANT_ERROR)) {
        this.retryCount = MAX_RETRY_ATTEMPTS
      }
      this.handleRenewalFailure()
      throw error
    }
  }

  private async handleRenewalFailure() {
    this.retryCount++

    if (this.retryCount < MAX_RETRY_ATTEMPTS) {
      const delayMs = RETRY_DELAY_MS * this.retryCount
      setTimeout(() => {
        this.performProactiveRenewal()
      }, delayMs)
    } else {
      await this.userManager.removeUser()
      this.redirectToLogin()
    }
  }

  private redirectToLogin() {
    if (
      !window.location.pathname.includes(LOGIN_PATH) &&
      !window.location.pathname.includes(LOGIN_REDIRECT_PATH) &&
      !window.location.pathname.includes('/register/') &&
      !window.location.pathname.includes('/public/')
    ) {
      this.userManager.signinRedirect({
        state: window.location.pathname,
      })
    }
  }

  async ensureValidToken(): Promise<boolean> {
    try {
      const user = await this.userManager.getUser()
      if (!user || user.expired) {
        await this.performProactiveRenewal()
        return false
      }

      const expiresAt = user.expires_at * 1000
      const timeUntilExpiry = expiresAt - Date.now()
      const bufferTime = RENEWAL_BUFFER_MINUTES * 60 * 1000

      if (timeUntilExpiry <= bufferTime) {
        await this.performProactiveRenewal()
        return false
      }

      return true
    } catch (error) {
      return false
    }
  }

  cleanup() {
    if (this.renewalTimer) {
      clearTimeout(this.renewalTimer)
      this.renewalTimer = null
    }
  }
}

const tokenManager = new TokenManager(configuration)

const getUserWhenLogin = async () => {
  if (!tokenManager['isInitialized']) {
    tokenManager['isInitialized'] = true
  }

  let user
  const userDetails = await configuration.getUser()
  if (userDetails && !userDetails.expired) {
    user = userDetails
    setActiveAccessToken(userDetails.access_token, userDetails.expires_at * 1000)
    tokenManager.initializeRenewalSchedule(userDetails.expires_at * 1000)
  } else {
    if (!window.location.pathname.includes('/register/')) {
      configuration.signinRedirect()
    }
  }
  return user
}

const getUser = async () => {
  await configuration.clearStaleState()
  try {
    const user = await configuration.getUser()
    if (user && !user.expired) {
      setActiveAccessToken(user.access_token, user.expires_at * 1000)
      return user
    } else {
      throw new Error()
    }
  } catch (error) {
    if (
      !window.location.pathname.includes(LOGIN_PATH) &&
      !window.location.pathname.includes(LOGIN_REDIRECT_PATH) &&
      !window.location.pathname.includes('/register/')
    ) {
      configuration.signinRedirect({ state: window.location.pathname })
    }
    return null
  }
}

const renewToken = async () => {
  try {
    const isValid = await tokenManager.ensureValidToken()
    if (!isValid) {
      const user = await configuration.getUser()
      if (user && !user.expired) {
        return user
      }
    }
    const currentUser = await configuration.getUser()
    if (currentUser && !currentUser.expired) {
      return currentUser
    }
  } catch (error) {
    if (
      !window.location.pathname.includes(LOGIN_PATH) &&
      !window.location.pathname.includes(LOGIN_REDIRECT_PATH) &&
      !window.location.pathname.includes('/register/')
    ) {
      configuration.signinRedirect({
        state: window.location.pathname,
      })
    }
    throw error
  }
}

const makeAuthenticatedRequest = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  return apiCall()
}

const signIn = () => {
  configuration.signinRedirect()
}

const signOut = async () => {
  tokenManager.cleanup()
  await configuration.removeUser()
  await configuration.signoutRedirect()
  await configuration.signoutCallback()
}

const userManager = {
  configuration,
  getUserWhenLogin,
  signIn,
  getUser,
  renewToken,
  signOut,
  makeAuthenticatedRequest,
  ensureValidToken: () => tokenManager.ensureValidToken(),
}

export default userManager
